import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { workerService } from '../services/workerService';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { formatShortDate } from '../utils/formatters';

export default function WorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const data = await workerService.getAll();
      setWorkers(data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await workerService.delete(deleteId);
      setWorkers(workers.filter((w) => w.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting worker:', error);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'image_url',
      label: 'Фото',
      render: (value) => (
        <img
          src={value || 'https://via.placeholder.com/50'}
          alt="Worker"
          className="h-12 w-12 rounded-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/50';
          }}
        />
      ),
    },
    {
      key: 'first_name',
      label: 'Имя',
      render: (value, row) => (
        <span className="font-medium">{`${value} ${row.last_name}`}</span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'extra_role',
      label: 'Должность',
      render: (value) => value || '-',
    },
    {
      key: 'created_at',
      label: 'Дата добавления',
      render: (value) => formatShortDate(value),
    },
  ];

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Сотрудники</h1>
        <Link to="/admin/workers/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Добавить сотрудника
          </Button>
        </Link>
      </div>

      <Table
        columns={columns}
        data={workers}
        loading={loading}
        actions={(row) => (
          <div className="flex items-center justify-end gap-2">
            <Link
              to={`/admin/workers/${row.id}/edit`}
              className="text-primary-600 hover:text-primary-800"
              title="Редактировать"
            >
              <Edit className="h-5 w-5" />
            </Link>
            <button
              onClick={() => setDeleteId(row.id)}
              className="text-red-500 hover:text-red-700"
              title="Удалить"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        )}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Удаление сотрудника"
        message="Вы уверены, что хотите удалить этого сотрудника? Это действие нельзя отменить."
        confirmText="Удалить"
        loading={deleting}
      />
    </div>
  );
}
